// API Configuration utility
const isLocalHost = typeof window !== 'undefined' && (
  window.location.hostname === 'localhost' ||
  window.location.hostname === '127.0.0.1' ||
  window.location.port === '3000'
);
const API_BASE_URL = isLocalHost ? 'http://localhost:8008/api' : '/api';

export const apiConfig = {
  baseURL: API_BASE_URL,
  
  // Helper function to build full API URLs
  buildURL: (endpoint) => {
    // Remove leading slash if present
    const cleanEndpoint = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint;
    return `${API_BASE_URL}/${cleanEndpoint}`;
  },
  
  // Common endpoints
  endpoints: {
    auth: {
      login: () => apiConfig.buildURL('auth/login/'),
      logout: () => apiConfig.buildURL('auth/logout/'),
      csrf: () => apiConfig.buildURL('auth/csrf/'),
      user: () => apiConfig.buildURL('auth/user/'),
    },
    clones: {
      list: () => apiConfig.buildURL('clones/'),
      exportAccess: () => apiConfig.buildURL('clones/export-access/'),
      detail: (id) => apiConfig.buildURL(`clones/${id}/`),
      attributes: (id) => apiConfig.buildURL(`clones/${id}/attributes/`),
      exportAttributes: (id) => apiConfig.buildURL(`clones/${id}/export-attributes/`),
      activation: (id) => apiConfig.buildURL(`clones/${id}/activation/`),
      activationStart: (id) => apiConfig.buildURL(`clones/${id}/activation/start/`),
      restoreBackup: (id) => apiConfig.buildURL(`clones/${id}/restore-backup/`),
      fixOwnership: (id) => apiConfig.buildURL(`clones/${id}/fix-ownership/`),
      fixOwnershipBulk: () => apiConfig.buildURL('clones/fix-ownership-bulk/'),
      paymentAttempts: (id) => apiConfig.buildURL(`clones/${id}/payment-attempts/`),
      notifications: (id) => apiConfig.buildURL(`clones/${id}/notifications/`),
      delete: (id) => apiConfig.buildURL(`clones/${id}/`),
    },
    dns: {
      listSubdomains: () => apiConfig.buildURL('dns/subdomains/'),
      checkSubdomain: (name) => apiConfig.buildURL(`dns/check-subdomain/?name=${encodeURIComponent(name)}`),
    },
    modules: {
      list: () => apiConfig.buildURL('modules/'),
      detail: (id) => apiConfig.buildURL(`modules/${id}/`),
    },
    tariffs: {
      list: () => apiConfig.buildURL('tariffs/'),
      detail: (id) => apiConfig.buildURL(`tariffs/${id}/`),
      copy: (id) => apiConfig.buildURL(`tariffs/${id}/copy/`),
    },
    notifications: {
      list: (limit = 100) => apiConfig.buildURL(`billing/notifications/?limit=${limit}`),
      preview: (id) => apiConfig.buildURL(`billing/notifications/${id}/preview/`),
      samples: () => apiConfig.buildURL('billing/email-previews/'),
      sendSamples: () => apiConfig.buildURL('billing/email-previews/send/'),
    },
    billingEvents: {
      list: (limit = 100, eventType = '') => {
        const query = new URLSearchParams({ limit: String(limit) });
        if (eventType) query.set('event_type', eventType);
        return apiConfig.buildURL(`billing/events/?${query.toString()}`);
      },
      clone: (id, options = {}) => {
        const opts = typeof options === 'number' ? { limit: options } : options || {};
        const query = new URLSearchParams();
        if (opts.page) query.set('page', String(opts.page));
        if (opts.pageSize) query.set('page_size', String(opts.pageSize));
        else if (opts.limit) query.set('limit', String(opts.limit));
        const suffix = query.toString() ? `?${query.toString()}` : '';
        return apiConfig.buildURL(`clones/${id}/billing-events/${suffix}`);
      },
    },
    paymentAttempts: {
      list: ({ provider = '', channel = '', status = '', cloneId = '', page = 1, pageSize = 25 } = {}) => {
        const query = new URLSearchParams({
          page: String(page),
          page_size: String(pageSize)
        });
        if (provider) query.set('provider', provider);
        if (channel) query.set('channel', channel);
        if (status) query.set('status', status);
        if (cloneId) query.set('clone_id', String(cloneId));
        return apiConfig.buildURL(`billing/payment-attempts/?${query.toString()}`);
      }
    },
    tbankBilling: {
      payerDetails: (uuid) => apiConfig.buildURL(`billing/payer-details/${uuid}/`),
      partyLookup: (uuid) => apiConfig.buildURL(`billing/party-lookup/${uuid}/`),
      subscription: (uuid) => apiConfig.buildURL(`billing/subscription/${uuid}/`),
      subscriptionCancel: (uuid) => apiConfig.buildURL(`billing/subscription/${uuid}/cancel/`),
      createInvoice: () => apiConfig.buildURL('billing/invoices/'),
      resendInvoice: (invoiceId) => apiConfig.buildURL(`billing/invoices/${invoiceId}/resend/`),
      reconciliationResolve: (attemptId) => apiConfig.buildURL(`billing/reconciliation/${attemptId}/resolve/`),
    },
    attributes: {
      list: () => apiConfig.buildURL('attributes/'),
      detail: (id) => apiConfig.buildURL(`attributes/${id}/`),
      groups: () => apiConfig.buildURL('attribute-groups/'),
      groupDetail: (id) => apiConfig.buildURL(`attribute-groups/${id}/`),
    },
    generationAnalytics: {
      runs: (query) => {
        const raw = query || '';
        const q = raw.startsWith('?') ? raw.slice(1) : raw;
        return apiConfig.buildURL(`generation-analytics/runs/${q ? `?${q}` : ''}`);
      },
      logs: (query) => {
        const raw = query || '';
        const q = raw.startsWith('?') ? raw.slice(1) : raw;
        return apiConfig.buildURL(`generation-analytics/logs/${q ? `?${q}` : ''}`);
      },
      aggregates: (query) => {
        const raw = query || '';
        const q = raw.startsWith('?') ? raw.slice(1) : raw;
        return apiConfig.buildURL(`generation-analytics/log-aggregates/${q ? `?${q}` : ''}`);
      },
      scatterPoints: (query) => {
        const raw = query || '';
        const q = raw.startsWith('?') ? raw.slice(1) : raw;
        return apiConfig.buildURL(`generation-analytics/log-scatter-points/${q ? `?${q}` : ''}`);
      },
      phaseTimeValuePoints: (query) => {
        const raw = query || '';
        const q = raw.startsWith('?') ? raw.slice(1) : raw;
        return apiConfig.buildURL(`generation-analytics/log-phase-time-value-points/${q ? `?${q}` : ''}`);
      },
      filterOptions: () => apiConfig.buildURL('generation-analytics/filter-options/'),
      successMetricsTiers: (query) => {
        const raw = query || '';
        const q = raw.startsWith('?') ? raw.slice(1) : raw;
        return apiConfig.buildURL(`generation-analytics/success-metrics/tiers/${q ? `?${q}` : ''}`);
      },
      successMetricsRuns: (query) => {
        const raw = query || '';
        const q = raw.startsWith('?') ? raw.slice(1) : raw;
        return apiConfig.buildURL(`generation-analytics/success-metrics/runs/${q ? `?${q}` : ''}`);
      },
      sync: () => apiConfig.buildURL('generation-analytics/sync/'),
    },
    schedulerMl: {
      options: () => apiConfig.buildURL('scheduler-ml/options/'),
      trainingRuns: () => apiConfig.buildURL('scheduler-ml/training-runs/'),
      trainingRunDetail: (id) => apiConfig.buildURL(`scheduler-ml/training-runs/${id}/`),
      trainingRunArtifact: (id) => apiConfig.buildURL(`scheduler-ml/training-runs/${id}/artifact/`),
      policies: () => apiConfig.buildURL('scheduler-ml/policies/'),
      policyActivate: (id) => apiConfig.buildURL(`scheduler-ml/policies/${id}/activate/`),
      modelVersions: () => apiConfig.buildURL('scheduler-ml/model-versions/'),
      advisorVersions: () => apiConfig.buildURL('scheduler-ml/advisor-versions/'),
      metaPolicyVersions: () => apiConfig.buildURL('scheduler-ml/meta-policy-versions/'),
      metaPolicyEvaluate: () => apiConfig.buildURL('scheduler-ml/meta-policy-evaluate/'),
      experiments: () => apiConfig.buildURL('scheduler-ml/experiments/'),
      exportBundle: () => apiConfig.buildURL('scheduler-ml/export-bundle/')
    },
    generationQueue: {
      priorities: () => apiConfig.buildURL('generation-queue/priorities/')
    },
  }
};

/** DRF TokenAuthentication (см. AuthContext / CloneForm). */
export function getAuthHeaders() {
  if (typeof localStorage === 'undefined') {
    return {};
  }
  const token = localStorage.getItem('authToken');
  return token ? { Authorization: `Token ${token}` } : {};
}

export default apiConfig;
